import { supabase } from '../supabaseClient';
import type {
  BetaEventCodeRequestInput,
  BetaEventCodeRequestRow,
  BetaEventCodeRequestStatus,
} from '../types/betaEventCodeRequest';

const SELECT_COLS =
  'id, full_name, email, organization, note, user_id, status, issued_code, reject_message, created_at, updated_at';

function mapRow(row: Record<string, unknown>): BetaEventCodeRequestRow {
  const statusRaw = typeof row.status === 'string' ? row.status : 'pending';
  const status: BetaEventCodeRequestStatus =
    statusRaw === 'done' || statusRaw === 'rejected' ? statusRaw : 'pending';
  return {
    id: String(row.id ?? ''),
    fullName: typeof row.full_name === 'string' ? row.full_name : '',
    email: typeof row.email === 'string' ? row.email : '',
    organization: typeof row.organization === 'string' ? row.organization : '',
    note: typeof row.note === 'string' ? row.note : '',
    userId: typeof row.user_id === 'string' ? row.user_id : null,
    status,
    issuedCode: typeof row.issued_code === 'string' ? row.issued_code : '',
    rejectMessage: typeof row.reject_message === 'string' ? row.reject_message : '',
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

function normalizeInput(input: BetaEventCodeRequestInput): {
  full_name: string;
  email: string;
  organization: string;
  note: string;
  user_id: string | null;
} {
  const fullName = input.fullName.trim().slice(0, 80);
  const email = input.email.trim().toLowerCase().slice(0, 320);
  const organization = input.organization.trim().slice(0, 200);
  const note = (input.note ?? '').trim().slice(0, 1000);
  if (!fullName) throw new Error('이름을 입력해 주세요.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error('올바른 이메일을 입력해 주세요.');
  }
  if (!organization) throw new Error('소속을 입력해 주세요.');
  return {
    full_name: fullName,
    email,
    organization,
    note,
    user_id: input.userId?.trim() || null,
  };
}

export type SubmitBetaEventCodeRequestResult =
  | {
      ok: true;
      id: string;
      issued: boolean;
      issuedCode: string | null;
      fullName: string;
      email: string;
      organization: string;
      status: string;
      reason?: string;
    }
  | { ok: false; error: string; alreadyRequested?: boolean; notAuthenticated?: boolean };

function mapSubmitRpcPayload(data: unknown): SubmitBetaEventCodeRequestResult {
  if (!data || typeof data !== 'object') {
    return { ok: false, error: 'EMPTY_RPC_RESPONSE' };
  }
  const row = data as Record<string, unknown>;
  if (row.ok === false) {
    return { ok: false, error: String(row.error ?? row.reason ?? 'SUBMIT_FAILED') };
  }
  return {
    ok: true,
    id: String(row.id ?? ''),
    issued: Boolean(row.issued),
    issuedCode: typeof row.issued_code === 'string' ? row.issued_code : null,
    fullName: typeof row.full_name === 'string' ? row.full_name : '',
    email: typeof row.email === 'string' ? row.email : '',
    organization: typeof row.organization === 'string' ? row.organization : '',
    status: typeof row.status === 'string' ? row.status : 'pending',
    reason: typeof row.reason === 'string' ? row.reason : undefined,
  };
}

/**
 * 베타 이벤트 코드 요청 접수.
 * DB `auto_send_enabled`이면 활성 CBT- 코드를 즉시 발급(status=done)하고 issuedCode를 반환한다.
 */
export async function submitBetaEventCodeRequest(
  input: BetaEventCodeRequestInput,
): Promise<SubmitBetaEventCodeRequestResult> {
  try {
    const row = normalizeInput(input);
    const { data, error } = await supabase.rpc('submit_beta_event_code_request', {
      p_full_name: row.full_name,
      p_email: row.email,
      p_organization: row.organization,
      p_note: row.note,
      p_user_id: row.user_id,
    });
    if (!error) return mapSubmitRpcPayload(data);

    const msg = error.message ?? String(error);
    return {
      ok: false,
      error: msg,
      alreadyRequested: /ALREADY_REQUESTED/.test(msg),
      notAuthenticated: /NOT_AUTHENTICATED/.test(msg),
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}

export type TryAutoIssueBetaEventCodeResult =
  | {
      ok: true;
      issued: boolean;
      id?: string;
      issuedCode?: string | null;
      fullName?: string;
      email?: string;
      organization?: string;
      reason?: string;
    }
  | { ok: false; error: string };

/** 관리자: 자동발송 ON일 때 pending 1건 발급 시도 */
export async function tryAutoIssueBetaEventCodeRequest(
  requestId: string,
): Promise<TryAutoIssueBetaEventCodeResult> {
  const { data, error } = await supabase.rpc('try_auto_issue_beta_event_code_request', {
    p_request_id: requestId,
  });
  if (error) return { ok: false, error: error.message ?? String(error) };
  if (!data || typeof data !== 'object') return { ok: false, error: 'EMPTY_RPC_RESPONSE' };
  const row = data as Record<string, unknown>;
  if (row.ok === false) {
    return { ok: false, error: String(row.reason ?? row.error ?? 'ISSUE_FAILED') };
  }
  return {
    ok: true,
    issued: Boolean(row.issued),
    id: typeof row.id === 'string' ? row.id : undefined,
    issuedCode: typeof row.issued_code === 'string' ? row.issued_code : null,
    fullName: typeof row.full_name === 'string' ? row.full_name : undefined,
    email: typeof row.email === 'string' ? row.email : undefined,
    organization: typeof row.organization === 'string' ? row.organization : undefined,
    reason: typeof row.reason === 'string' ? row.reason : undefined,
  };
}

export async function listBetaEventCodeRequests(limit = 1000): Promise<BetaEventCodeRequestRow[]> {
  const { data, error } = await supabase
    .from('platform_beta_event_code_requests')
    .select(SELECT_COLS)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
}

export async function updateBetaEventCodeRequestStatus(
  id: string,
  status: BetaEventCodeRequestStatus,
): Promise<BetaEventCodeRequestRow> {
  const { data, error } = await supabase
    .from('platform_beta_event_code_requests')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select(SELECT_COLS)
    .single();
  if (error) throw error;
  return mapRow(data as Record<string, unknown>);
}

export async function markBetaEventCodeRequestRejected(
  id: string,
  rejectMessage: string,
): Promise<BetaEventCodeRequestRow> {
  const message = rejectMessage.trim().slice(0, 4000);
  const { data, error } = await supabase
    .from('platform_beta_event_code_requests')
    .update({
      status: 'rejected',
      reject_message: message,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select(SELECT_COLS)
    .single();
  if (error) throw error;
  return mapRow(data as Record<string, unknown>);
}

export async function markBetaEventCodeRequestIssued(
  id: string,
  issuedCode: string,
): Promise<BetaEventCodeRequestRow> {
  const code = issuedCode.trim().toUpperCase().slice(0, 40);
  const { data, error } = await supabase
    .from('platform_beta_event_code_requests')
    .update({
      issued_code: code,
      status: 'done',
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select(SELECT_COLS)
    .single();
  if (error) throw error;
  return mapRow(data as Record<string, unknown>);
}

export async function deleteBetaEventCodeRequest(id: string): Promise<void> {
  const { error } = await supabase.from('platform_beta_event_code_requests').delete().eq('id', id);
  if (error) throw error;
}
